import { type DestroyRef, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  delayWhen,
  filter,
  map,
  type Observable,
  Subject,
  type SubscriptionLike,
  takeUntil,
  tap,
} from 'rxjs';

import {
  type ElementsState,
  runtimeActions,
  runtimeSelectors,
} from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementId,
  type HasDataProps,
} from '@blaze-runtime/engine-domain';

import { RenderElementService } from '../../services/render-element.service';
import { SchemaValidationService } from '../../services/schema-validation.service';
import { hasProps } from '../element-model/element-props-guards';
import { type ChildUpdateEvent, type RenderElementInstance } from '../models/render-element.models';

import { ElementInputsBinding } from './inputs/element-inputs-binding.service';
import {
  type ComponentChildrenHandler,
  type ComponentValidationHandler,
  type DataInputsReadyMap,
  type ElementDataOrchestratorConfig,
  type OutputEmitGuard,
} from './io-data.models';
import { ElementOutputStreamBuilder } from './outputs/element-output-stream-builder';

/**
 * currently the render-element.directive creates an instance of this service.
 * V2: static service will manage instances creation/destruction independent of the render-dir lifecycle.
 */
export class ElementIODataOrchestratorService {
  private readonly actions = inject(Actions);
  private readonly injector = inject(EnvironmentInjector);
  private readonly renderElementService = inject(RenderElementService);
  private readonly schemaValidationService = inject(SchemaValidationService);
  private readonly store = inject(Store<ElementsState>);
  private readonly elementOutputStreamBuilder = inject(ElementOutputStreamBuilder);

  private readonly isDataContainerValueSet$$ = new BehaviorSubject<boolean>(false);
  private readonly dataInitialized$$ = new BehaviorSubject<boolean>(false);
  private readonly dataChanged$$ = new Subject<CanvasElementId>();
  /** Element visibility - hide element from the layout based on the 'visible' binding */
  private readonly isHiddenByBinding$$ = new BehaviorSubject<boolean>(false);
  /** Used to know which inputs are ready - the data source they use fed them with some data */
  private readonly dataInputsReady$$ = new BehaviorSubject<DataInputsReadyMap>({});
  private readonly componentReinitialized$$ = new Subject<boolean>();

  private params: ElementDataOrchestratorConfig;
  private outputEmitGuard: OutputEmitGuard | undefined;
  private dataInputsHandler: ElementInputsBinding;
  private childrenHandler?: ComponentChildrenHandler;
  private validationHandler?: ComponentValidationHandler;

  private outputsSubscription?: SubscriptionLike;
  private inputsSubscription?: SubscriptionLike;
  private childrenSubscription?: SubscriptionLike;
  private validationSubscription?: SubscriptionLike;

  dataChanged$ = this.dataChanged$$.asObservable();
  dataInputsReady$ = this.dataInputsReady$$.asObservable();
  isHiddenByBinding$ = this.isHiddenByBinding$$.asObservable();
  isDataContainerValueSet$ = this.isDataContainerValueSet$$.asObservable();
  componentReinitialized$ = this.componentReinitialized$$.asObservable();

  constructor(private readonly destroyRef: DestroyRef) {}

  configure(params: ElementDataOrchestratorConfig): void {
    this.params = params;
    const { outputEmitGuard, childrenHandler, validationHandler } = this.params.elementDefinition;

    runInInjectionContext(this.injector, () => {
      this.outputEmitGuard = outputEmitGuard ? inject(outputEmitGuard) : undefined;
      this.dataInputsHandler = inject(ElementInputsBinding);
      if (childrenHandler) {
        this.childrenHandler = inject(childrenHandler);
      }
      if (validationHandler) {
        this.validationHandler = inject(validationHandler);
      }
    });

    this.setupOutputSubscription();
    this.setupInputBindings();
    this.setupChildrenSync();
    this.setupValidation();
  }

  updateParams(params: Partial<ElementDataOrchestratorConfig>): void {
    this.params = { ...this.params, ...params };
  }

  componentReinitialized(state: boolean): void {
    this.componentReinitialized$$.next(state);
  }

  dataInitialized(): void {
    this.dataInitialized$$.next(true);
  }

  dataChanged(elementId: CanvasElementId): void {
    this.dataChanged$$.next(elementId);
  }

  dataInputsReady(value: DataInputsReadyMap): void {
    this.dataInputsReady$$.next(value);
  }

  isHiddenByBinding(value: boolean): void {
    this.isHiddenByBinding$$.next(value);
  }

  reconnect({ resetData, resetChildren }: { resetData?: boolean; resetChildren?: boolean }): void {
    if (resetData) {
      this.setupInputBindings();
      this.setupValidation();
    }
    if (resetChildren) {
      this.setupChildrenSync();
    }
  }

  cleanup(): void {
    const subs = [
      this.outputsSubscription,
      this.inputsSubscription,
      this.childrenSubscription,
      this.validationSubscription,
    ];
    for (const sub of subs) {
      sub?.unsubscribe();
    }
  }

  /** RxJS operator: completes the stream when the component is destroyed, navigation starts, or the component reinitializes. */
  takeUntilDestroyedOrReloaded<T>(): (observable$: Observable<T>) => Observable<T> {
    return (observable$: Observable<T>): Observable<T> =>
      observable$.pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.renderElementService.navigationStarted$),
        takeUntil(this.componentReinitialized$),
      );
  }

  /** RxJS operator: completes the stream when element data changes (triggers reconnection). */
  takeUntilDataChanged<T>(): (observable$: Observable<T>) => Observable<T> {
    return (observable$: Observable<T>): Observable<T> =>
      observable$.pipe(takeUntil(this.dataChanged$$));
  }

  /** @deprecated Use takeUntilDestroyedOrReloaded() */
  untilDestroyedOrReloaded<T>(): (observable$: Observable<T>) => Observable<T> {
    return this.takeUntilDestroyedOrReloaded<T>();
  }

  /** @deprecated Use takeUntilDataChanged() */
  untilDataChanged<T>(): (observable$: Observable<T>) => Observable<T> {
    return this.takeUntilDataChanged<T>();
  }

  private setupOutputSubscription(): void {
    const elementInstanceOutput = this.params.elementInstance.output;
    if (!elementInstanceOutput) {
      return;
    }
    const { element, elementInstance, elementDefinition } = this.params;
    this.outputsSubscription?.unsubscribe();
    this.outputsSubscription = this.elementOutputStreamBuilder
      .getElementOutputEventStream({
        element,
        elementInstance,
        elementDefinition,
        rowContext$: this.params.elementContext$.pipe(map((ctx) => ctx?.rowContext)),
        validationHandler: this.validationHandler,
        isDataContainerValueSetNotifier: () => this.isDataContainerValueSet$$.next(true),
        outputEmitGuard: this.outputEmitGuard,
      })
      .pipe(
        tap((outputEvent) => {
          if (!outputEvent) {
            return;
          }
          const { event, uiEvent } = outputEvent ?? {};
          // Validate data and schema - use schema provided in the event to avoid unpredictable automations
          // The validation is delayed using setTimeout() as the schema declarations done directly before emitting the output
          // might not present in the state immediately.
          // This is due to the actions registering schemas are processed by the NgRx reducer by using the `queueScheduler`.
          // Because of this we need to ensure the declaration has been populated in the state before we perform the validation.
          // https://github.com/ngrx/platform/blob/main/modules/store/src/state.ts#L44
          setTimeout(() => {
            this.schemaValidationService.validateElementOutputEvent(element, {
              ...uiEvent,
              schema: event.schema,
            });
          });
          // Emit the event
          this.store.dispatch(runtimeActions.runtimeUiEvent(uiEvent));
        }),
        this.takeUntilDestroyedOrReloaded(),
      )
      .subscribe();
  }

  private setupInputBindings(): void {
    const {
      element,
      elementDefinition,
      elementInstance,
      componentInitialized$,
      elementContext$,
      setComponentInput,
      changesNotifier,
    } = this.params;
    const { ioConfig } = elementDefinition;

    if (!hasProps(element) || !elementInstance || !ioConfig?.inputs) {
      return;
    }

    const inputs$ = this.dataInputsHandler.bindInputs({
      element,
      elementDefinition,
      elementInstance: elementInstance as RenderElementInstance,
      elementInputs: ioConfig.inputs,
      componentInitialized$,
      dataInitialized$: this.dataInitialized$$.asObservable(),
      elementContext$: elementContext$,
      setComponentInput,
      changesNotifier,
      isDataContainerValueSetUpdater: () => this.isDataContainerValueSet$$.next(true),
      isHiddenByBindingUpdater: (value: boolean) => this.isHiddenByBinding$$.next(value),
      dataInputsReadyUpdater: (update: DataInputsReadyMap) => {
        this.dataInputsReady$$.next({
          ...this.dataInputsReady$$.value,
          ...update,
        });
      },
    });

    if (!inputs$) {
      console.log(`[${element.id}/${element.type}] no data inputs`);
      return;
    }

    this.inputsSubscription?.unsubscribe();
    this.inputsSubscription = inputs$
      .pipe(this.takeUntilDestroyedOrReloaded(), this.takeUntilDataChanged())
      .subscribe();
  }

  private setupChildrenSync(): void {
    if (!this.childrenHandler || !this.params.elementInstance) {
      return;
    }

    const { element, elementInstance } = this.params;
    const container = element as CanvasElement & HasDataProps;
    const inputsCache = this.childrenHandler.buildInputsCache(element);

    // NOTE form children changes update the model of the container
    const childrenUpdates$: ChildUpdateEvent = this.actions.pipe(
      ofType(runtimeActions.runtimeUiEvent),
      filter(
        (event) =>
          event.eventType === 'change' &&
          !event.updateSource?.startsWith('form-input') &&
          !event.updateSource?.startsWith('formulas-refresh') &&
          event.output === undefined,
      ),
      concatLatestFrom(() => [
        this.renderElementService.entities$,
        this.store.select(runtimeSelectors.selectFormContextById(container.id)),
      ]),
      delayWhen(() => this.isDataContainerValueSet$.pipe(filter(Boolean))),
      map(([event, elements, formContext]) => {
        const formElement = elements[container.id] as CanvasElement & HasDataProps;
        const inputElement = elements[event.elementId] as CanvasElement & HasDataProps;
        this.childrenHandler?.updateInputsCache({
          inputsCache,
          inputElement,
          formElement,
          elements,
        });
        return { formElement, inputElement, event, formContext };
      }),
      filter(
        ({ formElement, inputElement }) =>
          !!formElement && !!inputElement && !!inputsCache[inputElement.id],
      ),
    );

    this.childrenSubscription?.unsubscribe();
    this.childrenSubscription = this.childrenHandler
      .connectChildren({
        container,
        childrenUpdates$,
        inputsCache,
        formInstance: elementInstance as RenderElementInstance,
      })
      .pipe(takeUntil(this.params.wrappersChanged$), this.takeUntilDestroyedOrReloaded())
      .subscribe();
  }

  private setupValidation(): void {
    if (!this.validationHandler || !this.params.elementInstance) {
      return;
    }

    const { element, elementInstance, elementContext$, setComponentInput } = this.params;

    this.validationSubscription?.unsubscribe();
    this.validationSubscription = this.validationHandler
      .connectValidation({
        element,
        elementInstance: elementInstance as RenderElementInstance,
        elementContext$,
        setComponentInput,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        this.takeUntilDestroyedOrReloaded(),
        this.takeUntilDataChanged(),
      )
      .subscribe();
  }
}
